import { useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useSubscriptionById, useSubscriptionHistory } from "../services/detailApi";
import { useSubscriptionMetrics } from "@/pages/subscriptions/services/subscriptionsV2Api";
import { transformBackendToFrontend } from "../utils/subscriptionReverseTransformers";
import { SEARCH_PARAMS, StatusFilter, SubscriptionType } from "@/pages/subscriptions/constants";
import type { UseSubscriptionDetailReturn } from "../types";
import { buildEnrichedSubscription } from "../utils/enrichSubscriptionVariables";

// Statuses each subscription kind exposes in its filter dropdown. Used to
// normalize the URL ?status= param so a deep link with an invalid value
// (e.g. ?status=triggered on a report) self-heals to "all" rather than
// sending an invalid filter to /runs and returning an empty list.
const ALERT_VALID_STATUSES = new Set<string>([
  StatusFilter.ALL,
  StatusFilter.TRIGGERED,
  StatusFilter.SKIPPED,
  StatusFilter.FAILURE,
]);
const REPORT_VALID_STATUSES = new Set<string>([
  StatusFilter.ALL,
  StatusFilter.SENT,
  StatusFilter.FAILURE,
]);

export const useSubscriptionDetail = (subscriptionId?: string): UseSubscriptionDetailReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Default to "all" so the filter is valid for both alert and report
  // subscriptions (the dropdown is type-aware and does not expose every value
  // to every subscription kind).
  const rawStatusFilter = searchParams.get(SEARCH_PARAMS.STATUS) || StatusFilter.ALL;

  // Get dates from URL or use defaults (memoized to prevent unnecessary re-renders)
  const { startDateStr, endDateStr, startDate, endDate } = useMemo(() => {
    const urlStartDate = searchParams.get(SEARCH_PARAMS.START_DATE);
    const urlEndDate = searchParams.get(SEARCH_PARAMS.END_DATE);

    const start = urlStartDate
      ? dayjs(urlStartDate).toDate()
      : dayjs().subtract(30, "day").toDate();
    const end = urlEndDate ? dayjs(urlEndDate).toDate() : dayjs().toDate();

    return {
      startDateStr: start.toISOString(),
      endDateStr: end.toISOString(),
      startDate: start,
      endDate: end,
    };
  }, [searchParams]);

  // Calculate current period from dates
  const currentPeriod = useMemo(() => {
    return Math.ceil(dayjs(endDate).diff(dayjs(startDate), "days"));
  }, [startDate, endDate]);

  // Get pagination from URL (memoized)
  const { page, pageSize } = useMemo(() => {
    const urlPage = searchParams.get(SEARCH_PARAMS.PAGE);
    const urlPageSize = searchParams.get(SEARCH_PARAMS.PAGE_SIZE);

    return {
      page: urlPage ? Math.max(0, parseInt(urlPage) - 1) : 0,
      pageSize: urlPageSize ? parseInt(urlPageSize) : 10,
    };
  }, [searchParams]);

  // Track previous filter values to detect changes. Use the raw value here:
  // the page-reset effect only cares about user-driven filter changes, not
  // the post-validation normalization (which happens once on load).
  const prevFilterRef = useRef<string | null>(null);
  const filterKey = `${rawStatusFilter}|${startDateStr}|${endDateStr}`;

  // Reset page when filters change (not pagination)
  useEffect(() => {
    if (prevFilterRef.current === null) {
      // Initial mount - just store the filter key
      prevFilterRef.current = filterKey;
      return;
    }

    if (prevFilterRef.current !== filterKey && page !== 0) {
      // Filters changed, reset to page 1
      setSearchParams(
        prev => {
          prev.set(SEARCH_PARAMS.PAGE, "1");
          return prev;
        },
        { replace: true }
      );
    }

    prevFilterRef.current = filterKey;
  }, [filterKey, page, setSearchParams]);

  // Handle period selection
  const handlePeriodSelect = useCallback(
    (days: string) => {
      const newEndDate = dayjs().toDate();
      const newStartDate = dayjs().subtract(Number(days), "day").toDate();
      setSearchParams(
        prev => {
          prev.set(SEARCH_PARAMS.START_DATE, newStartDate.toISOString());
          prev.set(SEARCH_PARAMS.END_DATE, newEndDate.toISOString());
          prev.set(SEARCH_PARAMS.PAGE, "1"); // Reset to first page
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Handle status filter change
  const handleStatusFilterChange = useCallback(
    (value: string) => {
      setSearchParams(
        prev => {
          if (value === StatusFilter.ALL) {
            // ALL is the default — strip from URL for cleaner links.
            prev.delete(SEARCH_PARAMS.STATUS);
          } else {
            prev.set(SEARCH_PARAMS.STATUS, value);
          }
          // Reset to first page
          prev.set(SEARCH_PARAMS.PAGE, "1");
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Handler for page changes - updates URL params only
  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams(
        prev => {
          prev.set(SEARCH_PARAMS.PAGE, `${newPage + 1}`);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Handler for page size changes - updates URL params only
  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      setSearchParams(
        prev => {
          prev.set(SEARCH_PARAMS.PAGE_SIZE, `${newSize}`);
          prev.set(SEARCH_PARAMS.PAGE, "1");
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Reset page helper
  const resetPage = useCallback(() => {
    setSearchParams(
      prev => {
        prev.set(SEARCH_PARAMS.PAGE, "1");
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Fetch backend subscription data
  const { data: backendSubscription, isLoading, isError } = useSubscriptionById(subscriptionId);

  // Transform backend response to frontend format
  const subscription = useMemo(() => {
    if (!backendSubscription) return null;
    return transformBackendToFrontend(backendSubscription);
  }, [backendSubscription]);

  // Normalize the URL ?status= param against the valid set for the actual
  // subscription kind. Prevents a deep link like /subscriptions/<report-id>
  // ?status=triggered from silently sending an invalid filter to /runs.
  const validStatuses = useMemo(() => {
    if (subscription?.rule_type === SubscriptionType.ALERT) return ALERT_VALID_STATUSES;
    if (subscription?.rule_type === SubscriptionType.REPORT) return REPORT_VALID_STATUSES;
    return null; // subscription not yet loaded
  }, [subscription?.rule_type]);

  // While the subscription type is unknown, force "all" so we never fire an
  // invalid status against /runs before validation runs. Once the type loads,
  // accept the URL value only if it's in the valid set for that type.
  const statusFilter =
    validStatuses === null
      ? StatusFilter.ALL
      : validStatuses.has(rawStatusFilter)
        ? // validStatuses.has() confirms rawStatusFilter is a member of the enum.
          (rawStatusFilter as StatusFilter)
        : StatusFilter.ALL;

  // Strip an invalid status from the URL once we know the subscription kind.
  useEffect(() => {
    if (!validStatuses) return;
    if (validStatuses.has(rawStatusFilter)) return;
    setSearchParams(
      prev => {
        prev.delete(SEARCH_PARAMS.STATUS);
        return prev;
      },
      { replace: true }
    );
  }, [validStatuses, rawStatusFilter, setSearchParams]);

  // Fetch subscription metrics for display names
  const { data: metricsData } = useSubscriptionMetrics();

  // Fetch subscription history with status and date filters
  // Use stable string values to prevent unnecessary re-fetches
  const {
    data: historyData,
    isError: historyError,
    isFetching: isHistoryLoading,
  } = useSubscriptionHistory(
    subscriptionId,
    page + 1,
    pageSize,
    statusFilter !== StatusFilter.ALL ? statusFilter : undefined,
    startDateStr,
    endDateStr
  );

  // Create lookup maps for display names
  const { categoryDisplayNameLookup, metricDisplayNameLookup } = useMemo(() => {
    if (!metricsData) {
      return {
        categoryDisplayNameLookup: {} as Record<string, string>,
        metricDisplayNameLookup: {} as Record<string, string>,
      };
    }

    const categoryLookup: Record<string, string> = {};
    const metricLookup: Record<string, string> = {};

    metricsData.forEach(metric => {
      if (!categoryLookup[metric.category]) {
        categoryLookup[metric.category] = metric.category_display_name;
      }
      metricLookup[metric.name] = metric.display_name;
    });

    return {
      categoryDisplayNameLookup: categoryLookup,
      metricDisplayNameLookup: metricLookup,
    };
  }, [metricsData]);

  // Enrich subscription variables with display names
  const enrichedSubscription = useMemo(() => {
    if (!subscription) return null;
    return buildEnrichedSubscription(
      subscription,
      metricsData,
      categoryDisplayNameLookup,
      metricDisplayNameLookup
    );
  }, [subscription, categoryDisplayNameLookup, metricDisplayNameLookup, metricsData]);

  // Get history items from API
  const filteredHistory = useMemo(() => {
    if (!historyData?.items) return [];
    return historyData.items;
  }, [historyData]);

  // Calculate number of pages based on total from API
  const numPages = useMemo(() => {
    if (!historyData) return 0;
    return Math.ceil(historyData.total / pageSize);
  }, [historyData, pageSize]);

  return {
    enrichedSubscription,
    historyData,
    isLoading,
    isError,
    historyError,
    isHistoryLoading,
    statusFilter,
    currentPeriod,
    page,
    pageSize,
    numPages,
    filteredHistory,
    handleStatusFilterChange,
    handlePeriodSelect,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    resetPage,
  };
};
